import React from 'react';
import { RefinementListProps, useInstantSearch, useRefinementList } from 'react-instantsearch-hooks-web';
import Checkbox from 'components/commercetools-ui/atoms/checkbox';
import { FacetProps } from './types';
import useRefinementHelpers from '../../hooks/useRefinementHelpers';
import {
  CAPACITY_SIZE,
  LITRES_PER_MINUTE,
  OUTLET_LENGTH,
  PAN_HEIGHT,
  PAN_PROJECTION,
  RAIL_LENGTH,
  SHOWER_HEAD_SIZE,
} from 'helpers/constants/attributes';
import { getMeasurementBuckets, groupTermItemsByBucket } from 'helpers/utils/measurementBuckets';

const sortByName: RefinementListProps['sortBy'] = (a, b) => {
  return a.name.localeCompare(b.name, undefined, { numeric: true });
};

const TermFacet: React.FC<FacetProps> = ({ attribute }) => {
  const { refine, items } = useRefinementList({ attribute, limit: 999, sortBy: sortByName });
  const { resolveLabel } = useRefinementHelpers();
  const { setIndexUiState } = useInstantSearch();
  const buckets = getMeasurementBuckets(attribute);
  const isRangeFacet = attribute === 'searchableAttributes.range' || attribute.endsWith('.range') || attribute === 'attributes.range';
  const isColourFacet = attribute.toLowerCase().includes('colour') || attribute.toLowerCase().includes('color');
  const hasSingleSelection = (isRangeFacet || isColourFacet) && items.some((i) => i.isRefined);
  const visibleItems = hasSingleSelection ? items.filter((i) => i.isRefined) : items;

  const handleRefine = (term: any) => {
    if (isColourFacet) {
      if (term.isRefined) {
        refine(term.value);
      } else {
        items.filter((i) => i.isRefined && i.value !== term.value).forEach((i) => refine(i.value));
        refine(term.value);
      }
    } else {
      refine(term.value);
    }
  };

  // Apply/clear every value in the bucket in one state update, so it costs a single Algolia query.
  const handleBucketRefine = (bucketItems: typeof items, isRefined: boolean) => {
    const bucketValues = bucketItems.map((i) => i.value);
    setIndexUiState((prev) => {
      const current = prev.refinementList?.[attribute] ?? [];
      const nextValues = isRefined
        ? current.filter((v) => !bucketValues.includes(v))
        : Array.from(new Set([...current, ...bucketValues]));
      const refinementList = { ...prev.refinementList };
      if (nextValues.length > 0) refinementList[attribute] = nextValues;
      else delete refinementList[attribute];
      return { ...prev, refinementList, page: undefined };
    });
  };

  if (buckets) {
    return (
      <div>
        <div className="flex flex-col gap-18 px-2 py-16 lg:min-w-[20rem]">
          {groupTermItemsByBucket(buckets, items).map(({ bucket, items: bucketItems, isRefined }) => (
            <div
              onClick={() => handleBucketRefine(bucketItems, isRefined)}
              key={bucket.key}
              className="flex items-center justify-start gap-12 hover:cursor-pointer"
            >
              <Checkbox checked={isRefined} className="border-2 border-[#090909]" labelPosition="on-left" />
              <div className="text-16 font-normal text-[#090909] md:text-14 lg:w-[14rem] 2xl:text-16">
                {bucket.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col gap-18 px-2 py-16 lg:min-w-[20rem]">
        {visibleItems.map((term) => (
          <div
            onClick={() => handleRefine(term)}
            key={term.value}
            className="flex items-center justify-start gap-12 hover:cursor-pointer"
          >
            <Checkbox checked={term.isRefined} className="border-2 border-[#090909]" labelPosition="on-left" />
            <div className="text-16 font-normal text-[#090909] md:text-14 lg:w-[14rem] 2xl:text-16">
              {attribute.split('.').pop() === PAN_HEIGHT ||
              attribute.split('.').pop() === PAN_PROJECTION ||
              attribute.split('.').pop() === RAIL_LENGTH ||
              attribute.split('.').pop() === SHOWER_HEAD_SIZE ||  attribute.split('.').pop() === OUTLET_LENGTH
                ? `${resolveLabel(attribute, term.label)?.split('>')?.pop()}mm`
                : attribute.split('.').pop() === LITRES_PER_MINUTE
                ? `${resolveLabel(attribute, term.label)?.split('>')?.pop()}L/min`
                : attribute.split('.').pop() === CAPACITY_SIZE
                ? `${resolveLabel(attribute, term.label)?.split('>')?.pop()}L`
                : resolveLabel(attribute, term.label)?.split('>')?.pop() === 'true'
                ? 'Yes'
                : resolveLabel(attribute, term.label)?.split('>')?.pop() === 'false'
                ? 'No'
                : resolveLabel(attribute, term.label)?.split('>')?.pop()}
            </div>
            <div className="flex items-center gap-12"></div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TermFacet;
